/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */
// One event session of this week on the Regular Class List, from
// GET api/workshops/branch/:branchId/sessions. Laid out in the class table's
// columns; its only action is attendance, which coaches can take here.
// Expanding it lists the session's registered students with their status,
// from the attendance sheet the dialog uses.
import { Box, Collapse, IconButton, makeStyles, TableCell, TableRow } from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import axios from "axios";
import moment from "moment";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { Badge, OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { shallowEqual, useSelector } from "react-redux";
import { convertFrom24To12Format } from "../../../../helpers/TimeConvert";
import { EventAttendanceDialog } from "../../../Events/Components/EventAttendanceDialog";

// BatchRow's ratio colours.
const useStyles = makeStyles(() => ({
  warningRatio: {
    color: "white",
    background: "#eed202",
  },
  dangerRatio: {
    color: "white",
    background: "#F32013",
  },
  successRatio: {
    color: "white",
    background: "#4BB543",
  },
}));

const statusBadgeStyle = (backgroundColor) => ({
  backgroundColor,
  color: "white",
  borderRadius: "5px",
  padding: "5px 8px",
  fontSize: "12px",
});
const STATUS_COLOURS = {
  Present: "#4BB543",
  Absent: "#dc3545",
  Unmarked: "#a1a5b7",
};

// An event with no sessions appears with its own start/end time, which may be
// unset: show only the times there are, in the batch rows' 12-hour format.
const formatTimes = (start, end) =>
  [start, end].filter(Boolean).map(convertFrom24To12Format).join(" - ") || "—";

export default function EventSessionRow({
  row,
  reset,
  setReset,
  showActions,
  onAttendanceSaved,
}) {
  const classes = useStyles();
  const authToken = useSelector((state) => state.auth.authToken, shallowEqual);
  const [open, setOpen] = useState(false);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  // The session's attendance sheet ({ statuses, students }), loaded on every
  // expand (others may have marked it since; the last list stays up while it
  // loads) and again after the dialog saves.
  const [sheet, setSheet] = useState(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [sheetError, setSheetError] = useState("");
  const loadTicket = useRef(0);
  const date = moment(row.session_date, "YYYY-MM-DD");
  const isOpen = open && !reset;

  // A reply after the row is gone (page, filter or branch change) is dropped.
  useEffect(
    () => () => {
      loadTicket.current++;
    },
    []
  );

  // Spots like a batch row: registered out of capacity, or just the
  // registered count when the event has no limit.
  const max = row.max_students;
  const ratio = max ? row.registered_count / max : 0;

  const loadSheet = async () => {
    const ticket = ++loadTicket.current;
    setSheetLoading(true);
    setSheetError("");
    try {
      const res = await axios(`api/workshops/${row.workshop_id}/attendance/${row.session_key}`, {
        headers: { "x-auth-token": authToken },
      });
      if (ticket !== loadTicket.current) return;
      setSheet(res.data.data || {});
    } catch (e) {
      if (ticket !== loadTicket.current) return;
      console.error("event session students: ", e);
      setSheetError(e.response?.data?.message || "Could not load the students.");
    } finally {
      if (ticket === loadTicket.current) setSheetLoading(false);
    }
  };

  const handleRowClick = () => {
    const opening = !isOpen;
    setOpen(opening);
    reset && setReset(false);
    if (opening && !sheetLoading) loadSheet();
  };

  const statusBadge = (attendanceId) => {
    if (attendanceId == null) return <span className="text-muted">Not taken</span>;
    const name = (sheet.statuses || []).find((s) => s.attendance_id === attendanceId)?.name;
    return (
      <Badge style={statusBadgeStyle(STATUS_COLOURS[name] || STATUS_COLOURS.Unmarked)}>
        {name || "Other status"}
      </Badge>
    );
  };

  const students = sheet?.students || [];

  return (
    <Fragment>
      <TableRow hover tabIndex={-1}>
        <TableCell component="th" scope="row">
          <IconButton aria-label="expand row" size="small" onClick={handleRowClick}>
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
          <Badge variant="info">Event</Badge>
        </TableCell>
        <TableCell align="left" component="th" scope="row">
          <i className="far fa-calendar-alt mr-2" aria-hidden="true"></i>
          {row.workshop_name}
        </TableCell>
        <TableCell align="center">
          <Badge
            pill
            className={
              ratio < 0.4
                ? classes.successRatio
                : ratio < 0.7
                ? classes.warningRatio
                : classes.dangerRatio
            }
          >
            {max ? `${row.registered_count} / ${max}` : row.registered_count}
          </Badge>
        </TableCell>
        <TableCell align="center">{row.day}</TableCell>
        <TableCell align="center">{formatTimes(row.start_time, row.end_time)}</TableCell>
        <TableCell align="center">
          <div>{date.format("ddd MMM D")}</div>
          {row.registered_count > 0 && (
            <div className="text-muted">
              {row.marked_count} of {row.registered_count} marked
            </div>
          )}
          {row.note && <div className="text-muted">{row.note}</div>}
        </TableCell>
        {showActions && (
          <TableCell align="center">
            <OverlayTrigger
              overlay={<Tooltip id={`event-attendance-${row.key}`}>Open Attendance</Tooltip>}
            >
              <a
                className="btn btn-icon btn-light btn-hover-primary btn-sm mr-1"
                onClick={() => setAttendanceOpen(true)}
              >
                <i className="fa fa-user-check"></i>
              </a>
            </OverlayTrigger>
            <EventAttendanceDialog
              row={{
                workshop_id: row.workshop_id,
                workshop_name: row.workshop_name,
                startdate: row.session_date,
                start_time: row.start_time,
                end_time: row.end_time,
              }}
              initialSessionKey={row.session_key}
              lockSession
              show={attendanceOpen}
              onHide={() => setAttendanceOpen(false)}
              onSaved={() => {
                if (sheet || sheetLoading) loadSheet();
                onAttendanceSaved && onAttendanceSaved();
              }}
            />
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={showActions ? 7 : 6}>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box
              padding={2}
              margin={3}
              style={{ backgroundColor: "#f5f5f5", borderRadius: "0.5rem" }}
            >
              <h4>Registered Students{sheet ? ` (${students.length})` : ""}</h4>
              {sheetLoading && !sheet ? (
                <div className="d-flex align-items-center text-muted">
                  <Spinner animation="border" size="sm" className="mr-2" />
                  Loading students…
                </div>
              ) : sheetError ? (
                <p className="text-danger mb-0">{sheetError}</p>
              ) : students.length === 0 ? (
                <p className="text-muted mb-0">No registered students.</p>
              ) : (
                <table className="table table-sm table-vertical-center mb-0">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th style={{ width: 140 }} className="text-center">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s) => (
                      <tr key={s.user_id}>
                        <td>{s.student_name}</td>
                        <td className="text-center">{statusBadge(s.attendance_id)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </Fragment>
  );
}
